/**
 * Pure auth flow handlers (testable without React).
 *
 * Why this exists:
 * - We want regression coverage for critical auth UX (login -> verify email -> dashboard)
 *   without pulling DOM/test-rendering dependencies into this repo.
 * - Hooks should be thin adapters around deterministic handler logic.
 */

import type { ApiResponse, User } from '../../shared/types';
import { ApiError } from '../lib/api';
// Note: The handlers remain decoupled from TanStack Query.
// `useAuth.ts` injects the cache update behavior.

export type NotificationInput = {
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
};

type AuthFlowDeps = {
  setCachedCurrentUser: (user: User | null) => void;
  navigate: (to: string) => void;
  setUser: (user: User | null) => void;
  setPendingVerificationEmail: (email: string | null) => void;
  addNotification: (notification: NotificationInput) => void;
};

export function handleLoginSuccess(
  deps: AuthFlowDeps,
  response: ApiResponse<{ user: unknown; message: string }>,
  redirectTo?: string
): void {
  const user = response.data?.user as User;
  deps.setUser(user);
  deps.setCachedCurrentUser(user);
  deps.addNotification({
    type: 'success',
    message: 'Login realizado com sucesso!',
    duration: 3000,
  });
  deps.navigate(redirectTo || '/inicio');
}

export function handleLoginError(
  deps: AuthFlowDeps,
  error: ApiError,
  variables: { email: string; password: string; mfaCode?: string }
): void {
  const errorData = error.data as { verificationRequired?: boolean; mfaRequired?: boolean } | undefined;

  if (error.status === 403 && errorData?.verificationRequired) {
    deps.setPendingVerificationEmail(variables.email);
    deps.addNotification({
      type: 'warning',
      message: error.message || 'Verifique seu email para continuar.',
      duration: 6000,
    });
    deps.navigate('/verificar-email');
    return;
  }

  if (error.status === 400 && errorData?.mfaRequired) {
    deps.addNotification({
      type: 'warning',
      message: error.message || 'Código MFA obrigatório.',
      duration: 6000,
    });
    return;
  }

  deps.addNotification({
    type: 'error',
    message: error.message || 'Erro ao fazer login',
    duration: 5000,
  });
}

export function handleVerifyEmailSuccess(
  deps: AuthFlowDeps,
  response: ApiResponse<{ user: unknown; message?: string }>
): void {
  const user = response.data?.user as User;
  deps.setUser(user);
  deps.setPendingVerificationEmail(null);
  deps.setCachedCurrentUser(user);
  deps.addNotification({
    type: 'success',
    message: response.data?.message || 'Email verificado com sucesso!',
    duration: 5000,
  });
  deps.navigate('/inicio');
}

export function handleVerifyEmailError(deps: AuthFlowDeps, error: ApiError): void {
  deps.addNotification({
    type: 'error',
    message: error.message || 'Código inválido ou expirado',
    duration: 5000,
  });
}
